/**
 * Tab trigger class helpers — single source of truth for tab styling.
 * Used by TopNavigation (Link-based) and RightSidebarTabs (Radix TabsTrigger).
 * The tabs.tsx TabsTrigger base is intentionally minimal; styling lives here.
 */

const BASE =
  "relative flex h-full items-center px-4 text-sm font-medium no-underline transition-colors duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]";

/**
 * The 2px accent rule itself. `left-0 right-0` spans the tab's PADDED box, so the underline extends
 * past the label by whatever horizontal padding the tab carries — that overhang is what makes it read
 * as a tab rule rather than a text decoration. `bottom-[-1px]` lands it on the container's own 1px
 * hairline instead of floating above it.
 *
 * Exported because a third consumer (the Openings move selector) needs this exact geometry on non-Link,
 * non-Radix buttons with its own type scale. Duplicating it there produced a visibly different underline
 * — squared-off, flush to the text — which is the drift a shared constant exists to prevent.
 */
export const TAB_UNDERLINE =
  "after:absolute after:bottom-[-1px] after:left-0 after:right-0 after:h-[2px] after:bg-[var(--color-accent)] after:transition-opacity after:duration-200";

const UNDERLINE = TAB_UNDERLINE;

/** For <Link>/<a>-based tabs (TopNavigation). Inactive tabs sit a step brighter than the panel tabs below
 *  (--color-text-nav, #250): the bar is often over the landing hero or frosted glass. */
export function underlineTabClass(isActive: boolean): string {
  const state = isActive
    ? "text-on-surface after:opacity-100"
    : "text-[var(--color-text-nav)] bg-transparent hover:text-on-surface after:opacity-0";
  return `${BASE} ${UNDERLINE} ${state}`;
}

/** For Radix TabsTrigger — underline style (RightSidebarTabs, ImportDialog). */
export function underlineTabTriggerClass(): string {
  return [
    BASE,
    UNDERLINE,
    "pb-[9px] pt-0 h-auto rounded-none bg-transparent shadow-none border-0",
    "text-subdued hover:text-on-surface",
    "data-[state=active]:text-on-surface data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-transparent",
    "dark:data-[state=active]:bg-transparent dark:data-[state=active]:border-transparent",
    "after:opacity-0 data-[state=active]:after:opacity-100",
  ].join(" ");
}

/** For Radix TabsTrigger — pill/segmented style (e.g. platform toggle). */
export function pillTabTriggerClass(): string {
  return [
    "px-[14px] py-[6px] text-sm capitalize cursor-pointer border-none transition-colors duration-120",
    "text-faint bg-transparent hover:bg-white/[0.04] hover:text-on-surface font-medium",
    "data-[state=active]:bg-white/[0.08] data-[state=active]:text-on-surface data-[state=active]:font-semibold",
  ].join(" ");
}
